import {
  isActiveInMonth,
  isWithinRecurringTerm,
  type RecurringExpense,
  type UpcomingRecurring,
  type YearMonth,
} from '@financial/shared';

export type RecurringMonthLookupState = 'loading' | 'success' | 'error';

export type RecurringMonthStatusKind =
  | 'paused'
  | 'not-started'
  | 'finished'
  | 'not-scheduled'
  | 'checking'
  | 'unavailable'
  | 'registered'
  | 'scheduled'
  | 'due'
  | 'overdue'
  | 'insufficient-funds';

export interface RecurringMonthStatus {
  kind: RecurringMonthStatusKind;
  upcoming?: UpcomingRecurring;
}

/**
 * Estado de una regla en un mes concreto. La ausencia en `upcoming` sólo
 * significa "registrada" después de una respuesta exitosa para su moneda.
 */
export function recurringMonthStatus({
  item,
  month,
  lookupState,
  upcoming,
  hasFundingIssue = false,
}: {
  item: RecurringExpense;
  month: YearMonth;
  lookupState: RecurringMonthLookupState;
  upcoming?: UpcomingRecurring;
  hasFundingIssue?: boolean;
}): RecurringMonthStatus {
  if (!item.active) return { kind: 'paused' };
  if (item.startMonth > month) return { kind: 'not-started' };
  if (!isWithinRecurringTerm(item, month)) return { kind: 'finished' };
  if (!isActiveInMonth(item, month)) return { kind: 'not-scheduled' };
  if (lookupState === 'loading') return { kind: 'checking' };
  if (lookupState === 'error') return { kind: 'unavailable' };
  if (!upcoming) return { kind: 'registered' };
  if (hasFundingIssue) return { kind: 'insufficient-funds', upcoming };
  if (upcoming.daysUntil > 0) return { kind: 'scheduled', upcoming };
  if (upcoming.daysUntil === 0) return { kind: 'due', upcoming };
  return { kind: 'overdue', upcoming };
}

export function recurringMonthStatusPriority(status: RecurringMonthStatus): number {
  switch (status.kind) {
    case 'insufficient-funds':
      return 0;
    case 'overdue':
      return 1;
    case 'due':
      return 2;
    case 'scheduled':
      return 3;
    case 'checking':
    case 'unavailable':
      return 4;
    case 'registered':
      return 5;
    case 'not-started':
    case 'not-scheduled':
      return 6;
    case 'finished':
      return 7;
    case 'paused':
      return 8;
  }
}
